import { addLocale, setLocale as setUmiLocale } from "@@/plugin-locale";
import { getStoredAppLocale, setStoredAppLocale } from "@airange/shared/index-airange-admin";
import {
  type AppLocale,
  getLocale,
  normalizeLocale,
} from "@airange/shared/i18n-airange-admin-runtime";
import { createLocaleController } from "@airange/shared/locale";
import type { ConfigProviderProps } from "antd";
import { loadAntdLocale } from "../locales/antd";
import { loadUmiLocaleMessages } from "../locales/shared";

function toUmiLocale(locale: AppLocale) {
  if (locale === "en") return "en-US";
  if (locale === "ja") return "ja-JP";
  return "zh-CN";
}

function applyUmiLocale(locale: AppLocale, reload: boolean) {
  if (typeof window === "undefined") {
    return;
  }
  const umiLocale = toUmiLocale(locale);
  try {
    window.localStorage.setItem("umi_locale", umiLocale);
  } catch {
    // Ignore storage failures and still try to notify the page.
  }
  if (reload) {
    window.location.reload();
    return;
  }
  setUmiLocale(umiLocale, false);
}

const baseLocaleController = createLocaleController({
  getStoredLocale: () => getStoredAppLocale(),
  setStoredLocale: (locale) => setStoredAppLocale(locale),
});

const localeController = {
  ...baseLocaleController,
  initializeLocale: () => {
    const locale = baseLocaleController.initializeLocale();
    applyUmiLocale(locale, false);
    return locale;
  },
  switchLocale: (locale: string) => {
    const nextLocale = baseLocaleController.switchLocale(locale);
    applyUmiLocale(nextLocale, true);
    return nextLocale;
  },
};

let initialized = false;

export function initializePlatformAdminLocale() {
  if (initialized) {
    return getLocale();
  }
  initialized = true;
  return localeController.initializeLocale();
}

export async function loadPlatformAdminLocale(locale: AppLocale = getLocale()): Promise<{
  locale: AppLocale;
  antdLocale: NonNullable<ConfigProviderProps["locale"]>;
}> {
  const normalizedLocale = normalizeLocale(locale);
  const [messages, antdLocale] = await Promise.all([
    loadUmiLocaleMessages(normalizedLocale),
    loadAntdLocale(normalizedLocale),
  ]);
  addLocale(toUmiLocale(normalizedLocale), messages, {
    momentLocale: toUmiLocale(normalizedLocale),
    antd: antdLocale,
  });
  setUmiLocale(toUmiLocale(normalizedLocale), false);
  return { locale: normalizedLocale, antdLocale };
}

export function getStoredPlatformAdminLocale(): AppLocale {
  return getStoredAppLocale();
}

export const platformAdminLocaleController = localeController;
